import { useEffect, useId, useState, type FormEvent } from "react";
import { useCreateAgentSession, useLlmCredentials } from "../../api/queries";
import { Button } from "../../components/Button";
import { FormError } from "../../components/FormError";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import "../../components/Form.css";

interface StartBonnieSessionDialogProps {
  open: boolean;
  onClose: () => void;
}

const DEFAULT_BONNIE_IMAGE = "ghcr.io/comebye/bonnie-agent:latest";

/**
 * Starts a Bonnie (local, worktree-per-session) session — `local: true` on
 * `createAgentSession` (ADR-0039 P3/P4, already live server-side). Unlike
 * `LaunchWorkbenchWizard`, there's no "enroll a host" step: a Bonnie session
 * targets the developer's OWN machine, enrolled by running `comebye bonnie`
 * locally (a CLI/daemon action, not something this dialog can do for them) —
 * if no local node is enrolled yet, the create call fails and that failure
 * is rendered here, not silently retried as a hosted launch.
 */
export function StartBonnieSessionDialog({ open, onClose }: StartBonnieSessionDialogProps) {
  const labelId = useId();
  const credentialId = useId();
  const branchId = useId();
  const imageId = useId();
  const { pushToast } = useToast();
  const mutation = useCreateAgentSession();
  const { data: credentials } = useLlmCredentials();
  const [label, setLabel] = useState("");
  const [selectedCredential, setSelectedCredential] = useState("");
  const [branch, setBranch] = useState("");
  const [image, setImage] = useState(DEFAULT_BONNIE_IMAGE);

  const credentialItems = credentials?.items ?? [];

  useEffect(() => {
    if (credentialItems.length === 1 && !selectedCredential) {
      setSelectedCredential(credentialItems[0].id);
    }
  }, [credentialItems, selectedCredential]);

  function handleClose() {
    setLabel("");
    setSelectedCredential("");
    setBranch("");
    setImage(DEFAULT_BONNIE_IMAGE);
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const submittedLabel = label.trim();
    mutation.mutate(
      {
        credentialId: selectedCredential,
        image: image.trim(),
        label: submittedLabel,
        local: true,
        branch: branch.trim() || undefined,
      },
      {
        onSuccess: (created) => {
          pushToast({ title: `Bonnie session "${created.label}" started.`, tone: "success" });
          handleClose();
        },
      },
    );
  }

  return (
    <Modal open={open} title="Start a Bonnie session" onClose={handleClose} initialFocusId={labelId}>
      <form onSubmit={handleSubmit} noValidate>
        <label className="form-field" htmlFor={labelId}>
          <span>Session label</span>
          <input
            id={labelId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            required
            maxLength={128}
            placeholder="e.g. Add rate limiting"
          />
        </label>

        <label className="form-field" htmlFor={credentialId}>
          <span>LLM credential</span>
          <select
            id={credentialId}
            value={selectedCredential}
            onChange={(e) => setSelectedCredential(e.target.value)}
            required
          >
            <option value="" disabled>
              Select a credential…
            </option>
            {credentialItems.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label} ({c.provider})
              </option>
            ))}
          </select>
          {credentialItems.length === 0 && (
            <span className="form-field__hint">No LLM credentials configured. Add one first.</span>
          )}
        </label>

        <label className="form-field" htmlFor={branchId}>
          <span>Branch (optional)</span>
          <input
            id={branchId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={branch}
            onChange={(e) => setBranch(e.target.value)}
            maxLength={200}
            placeholder="Leave blank to let Bonnie name a branch for you"
          />
          <span className="form-field__hint">
            The session runs on its own git worktree for this branch, on your machine.
          </span>
        </label>

        <label className="form-field" htmlFor={imageId}>
          <span>Container image</span>
          <input
            id={imageId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={image}
            onChange={(e) => setImage(e.target.value)}
            required
            maxLength={512}
          />
          <span className="form-field__hint">Docker image for the coding agent container.</span>
        </label>

        {mutation.isError && mutation.error && (
          <FormError>{mutation.error.message || "Failed to start session."}</FormError>
        )}

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            disabled={mutation.isPending || !label.trim() || !selectedCredential || !image.trim()}
          >
            {mutation.isPending ? "Starting…" : "Start session"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
